import { useEffect } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { getRenderingEngine } from '@cornerstonejs/core';
import styled from '@emotion/styled';
import { Refresh } from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useResizeDetector } from 'react-resize-detector';

import type { LatestImage } from '../../../@types/latestImages';
import { annotationStyles } from '../../../theme/annotationStyles';
import { renderingEngineAtom } from '../../../utils/cornerstone/cornerstoneAtoms';
import { RENDERING_ENGINE } from '../../../utils/cornerstone/initializeCornerstone';
import { useCornerstone } from '../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../utils/cornerstone/useDicomCanvas';
import annotatorConstants from '../../annotator/annotator.constants';
import { viewerModeAtom } from '../../annotator/atoms';
import { isHQEnabledAtom } from '../../annotator/multiFileViewer/imageToolbar/atoms';
import { useRotation } from '../../annotator/multiFileViewer/RotationContext';

import { ModelMessaging } from './modelMessaging/ModelMessaging';

interface ImageProps {
  activeImage?: LatestImage;
  additionalImage?: boolean;
  section?: 'left' | 'right';
  index?: number;
}

const StyleWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${annotationStyles}
  .additionalImage {
    pointer-events: none;
  }
  .cornerstoneElement {
    width: 100%;
    height: 100%;
    display: flex;
    position: relative;
    text-align: left;
  }
  .loading-spinner-additional-image {
    position: absolute;
    left: 0;
    right: 0;
    margin-left: 0;
    margin-right: 0;
    display: flex;
    justify-content: center;
    z-index: 10;
  }
  .errorIcon {
    color: red;
    position: absolute;
  }
  .magnifyMode {
    cursor: zoom-in !important;
  }

  .rotateButton {
    z-index: 1000;
    position: absolute;
    margin: 0 auto;
    bottom: 2em;
    width: 2em;
    height: 2em;
    left: 0;
    right: 0;
  }

  .thumbnail-view {
    & circle {
      stroke-width: 0;
      r: 0.1em;
    }
    & line {
      stroke-dasharray: 4, 0;
      stroke-width: 0.08em;
    }
    & rect {
      stroke-width: 0.08em;
    }
    & ellipse {
      stroke-width: 0.08em;
    }
    & ellipse[stroke-dasharray='3,3'] {
      stroke-width: 0.08em;
      stroke-dasharray: 6, 0;
    }
  }
  .mainHistoryImage {
    & circle {
      stroke-width: 0;
      r: 0.18em;
    }
    & line {
      stroke-dasharray: 2, 2;
      stroke-width: 0.12em;
    }
    & rect {
      stroke-width: 0.08em;
    }
    & ellipse {
      stroke-width: 0.12em;
      &[stroke-dasharray] {
        stroke-width: 0.12em;
        stroke-dasharray: 2, 2;
      }
    }
  }
`;

const PopoverStyles = styled(Box)`
  .popoverText {
    padding: 3px;
    font-size: 12px;
  }
  .popoverPercentage {
    color: red;
    font-weight: bold;
  }
`;

export const Image = ({
  activeImage,
  additionalImage,
  section,
  index,
}: ImageProps) => {
  // atoms
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);

  const [viewerMode] = useAtom(viewerModeAtom);
  const [isHQEnabled] = useAtom(isHQEnabledAtom);

  // hooks
  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);

  const [dicomCanvasRef, setDicomCanvasRef] = useDicomCanvas();
  const { isCornerstoneReady, loadImageFileIntoCanvas, imageLoadStatus } =
    useCornerstone({
      isThumbnail: !!additionalImage,
      dicomCanvasRef,
      // @ts-expect-error TS(2322): Type 'VisitImage | undefined' is not assignable to... Remove this comment to see the full error message
      activeImage,
      viewportId: additionalImage
        ? 'additional-image' + index + section
        : 'image-history' + section,
      source: additionalImage ? 'additional-image' : 'compare-visits',
      side: section,
    });
  const { setIsRotationButtonHovered, rotateImage } = useRotation();

  // handlers
  const handleResize = () => {
    if (dicomCanvasRef && renderingEngine) {
      renderingEngine.resize();
    }
  };
  const { ref: containerRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    skipOnMount: true,
    refreshMode: undefined,
    onResize: handleResize,
  });

  const imageClasses = [
    'cornerstoneElement',
    viewerMode === annotatorConstants.MODES.MAGNIFYING
      ? 'magnifyMode'
      : 'default',
    additionalImage ? 'additionalImage' : '',
    additionalImage ? 'thumbnail-view' : '',
    !additionalImage ? 'mainHistoryImage' : '',
  ];

  const cariesDiseaseProgression20 = {
    text: 'Caries size',
    percentage: '+60%',
  };
  const rblDiseaseProgression = { text: 'RBL mm', percentage: '+1.0mm' };
  const cariesDiseaseProgression80 = {
    text: 'Caries size',
    percentage: '+60%',
  };
  const salesDemoDiseaseProgression = {
    '65e7f1b4875af15727dce93d': [
      {
        ...cariesDiseaseProgression20,
        xPosition: 265,
        yPosition: 225,
      },
      { ...rblDiseaseProgression, xPosition: 100, yPosition: 140 },
    ],
    '65b1353abcc8271970fab28b': [
      {
        ...cariesDiseaseProgression20,
        xPosition: 305,
        yPosition: 160,
      },
    ],
    '65e7f1b4f04651f0463b9334': [
      {
        ...cariesDiseaseProgression80,
        xPosition: 220,
        yPosition: 120,
      },
    ],
    '65e7f1b435a2a5ace608759e': [
      {
        ...cariesDiseaseProgression80,
        xPosition: 240,
        yPosition: 120,
      },
    ],
    '65e7f1b412807d8b7a4bdb9a': [
      {
        ...cariesDiseaseProgression80,
        xPosition: 220,
        yPosition: 120,
      },
    ],
  };

  const activeImageId = activeImage?.imageId;

  /**
   * When we have a nonempty dicom path, and cornerstone is loaded, we can load this image.
   */
  useEffect(() => {
    if (isCornerstoneReady && activeImage && dicomCanvasRef) {
      // Disable HQ if thumbnail image or if HQ is disabled by default
      loadImageFileIntoCanvas(!additionalImage && isHQEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'additionalImage' and 'loadImageFileIntoCanvas'. Either include them or remove the dependency array.
  }, [activeImage?.imageId, isCornerstoneReady, dicomCanvasRef, isHQEnabled]);

  return (
    <StyleWrapper ref={containerRef}>
      {imageLoadStatus === 'loading' && (
        <div className="loadingSpinnerAdditionalImage">
          <CircularProgress />
        </div>
      )}
      {imageLoadStatus === 'error' && <ErrorIcon className="errorIcon" />}
      {!additionalImage && (
        <div className="rotateButton">
          <IconButton
            onMouseEnter={() => {
              if (!activeImage?.imageId || !setIsRotationButtonHovered) {
                return;
              }
              setIsRotationButtonHovered((p) => [...p, activeImage.imageId]);
            }}
            onMouseLeave={() => {
              if (!activeImage?.imageId || !setIsRotationButtonHovered) {
                return;
              }
              setIsRotationButtonHovered((p) =>
                p.filter((v: any) => v !== activeImage?.imageId),
              );
            }}
            onClick={() => {
              rotateImage(activeImage);
            }}
            style={{
              backgroundColor: 'black',
              color: 'white',
              opacity: '0.6',
            }}
            
            size="small"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </div>
      )}
      <div
        ref={setDicomCanvasRef}
        className={imageClasses.join(' ')}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/** @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message */}
        {Object.keys(salesDemoDiseaseProgression).includes(activeImageId) &&
          !additionalImage &&
          section === 'right' &&
          // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
          salesDemoDiseaseProgression[activeImageId].map((pathology: any) => (
            <Popover
              anchorReference="anchorEl"
              open
              anchorEl={dicomCanvasRef}
              style={{
                pointerEvents: 'none',
                marginLeft: pathology.xPosition,
                marginTop: pathology.yPosition,
                width: 'auto',
              }}
            >
              <PopoverStyles display="flex" flexDirection="row" width="auto">
                <Typography className="popoverText">
                  {pathology.text}
                </Typography>
                <Typography className="popoverText popoverPercentage">
                  {pathology.percentage}
                </Typography>
              </PopoverStyles>
            </Popover>
          ))}
        {/** @ts-expect-error TS(2322): Type 'VisitImage | undefined' is not assignable to... Remove this comment to see the full error message */}
        {!additionalImage && <ModelMessaging activeImage={activeImage} />}
      </div>
    </StyleWrapper>
  );
};
